import ConfigJson from '@readcloud/common/config.json';
import { Button } from '@readcloud/component-library/components/Base/Button';
import {
  Label,
  TextArea,
} from '@readcloud/component-library/components/Base/Form';
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalTitle,
} from '@readcloud/component-library/components/Base/Modal';
import React from 'react';

import { applicationSelectors } from '@readcloud/state';
import { useSelector } from 'react-redux';

interface ImmersiveReaderProps {
  open?: boolean;
  selectedText: string;
  getImmersiveToken: () => Promise<any>;
  onCancel: () => void;
  toggleIOSInterceptor?: (interceptState: 'on' | 'off') => void;
}

interface ImmersiveWorkbook {
  open?: boolean;
  currentText?: string;
  handleImmersiveInput: (input: string) => void;
  handleOpenImmersive: (textToReader?: string) => void;
  onCancel: () => void;
  isOnline?: boolean;
  checkOnline?: () => boolean;
}

export type ReadAloudOptions = {
  voice?: string; // Voice, either 'male' or 'female'. Note that not all languages support both genders.
  speed?: number; // Playback speed, must be between 0.5 and 2.5, inclusive.
  autoplay?: boolean; // Automatically start Read Aloud when the Immersive Reader loads.
};

export type TranslationOptions = {
  language: string; // Set the translation language, e.g. fr-FR, es-MX, zh-Hans-CN. Required to automatically enable word or document translation.
  autoEnableDocumentTranslation?: boolean; // Automatically translate the entire document
  autoEnableWordTranslation?: boolean; // Automatically enable word translation
};

export enum ThemeOption {
  Light,
  Dark,
}

export type DisplayOptions = {
  textSize?: number; // Valid values are 14, 20, 28, 36, 42, 48, 56, 64, 72, 84, 96.
  increaseSpacing?: boolean; // Set whether increased spacing is enabled.
  fontFamily?: string; // Valid values are 'Calibri', 'ComicSans', and 'Sitka'
  themeOption?: ThemeOption; // option to set custom theme
};

interface Message {
  cogSvcsAccessToken: string | undefined;
  cogSvcsSubdomain: string;
  request: Content;
  launchToPostMessageSentDurationInMs: number;
  disableFirstRun?: boolean;
  readAloudOptions?: ReadAloudOptions;
  translationOptions?: TranslationOptions;
  displayOptions?: DisplayOptions;
  sendPreferences?: boolean;
  preferences?: string;
  disableGrammar?: boolean;
  disableTranslation?: boolean;
  disableLanguageDetection?: boolean;
}

interface Content {
  title?: string; // Title text shown at the top of the Immersive Reader (optional)
  chunks: Chunk[]; // Array of chunks
}

interface Chunk {
  content: string; // Plain text string
  lang?: string; // Language of the text, e.g. en, es-ES (optional). Language will be detected automatically if not specified.
  mimeType?: string; // MIME type of the content (optional). Currently 'text/plain', 'application/mathml+xml', and 'text/html' are supported. Defaults to 'text/plain' if not specified.
}

interface LaunchResponse {
  container: HTMLDivElement;
  sessionId: string;
}

interface Error {
  code: ErrorCode;
  message: string; // Human-readable representation of the error
  sessionId?: string; // Session ID which can be used for debugging
}

enum ErrorCode {
  BadArgument = 'BadArgument',
  Timeout = 'Timeout',
  TokenExpired = 'TokenExpired',
  Throttled = 'Throttled',
  ServerError = 'ServerError',
  InvalidSubdomain = 'InvalidSubdomain',
}

interface ImmersiveReaderResponse {
  success: boolean;
  errorCode?: ErrorCode;
  sessionId: string;
}

//Create Immersive Input Field
export const ImmersiveUserWorkbook = (props: ImmersiveWorkbook) => {
  const [workbookText, setWorkbookText] = React.useState<string>(
    props.currentText ?? ''
  );

  const [isOnlineLocal, setIsOnlineLocal] = React.useState<boolean>(
    props.isOnline ?? true
  );

  return (
    <Modal
      isOpen={props.open}
      size="xl"
      toggle={props.onCancel}
      backdrop={'static'}
    >
      <ModalHeader>
        <ModalTitle>Immersive Workbook</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Label htmlFor="immersive-workbook">
          Welcome to the Immersive Workbook.
        </Label>
        <p>
          Enter your text below and click 'Go' to start the Immersive Reader
        </p>

        <TextArea
          rows={4}
          name="immersive-workbook"
          className="rc-w-full rc-p-0 rc-max-h-full"
          onChange={(e: any) => {
            setWorkbookText(e.target.value);
          }}
          onBlur={(e: any) => {
            props.checkOnline && setIsOnlineLocal(props.checkOnline());
          }}
          value={workbookText}
        />

        {!isOnlineLocal ? (
          <p>
            <br />
            You must have an active internet connection in order to use this
            feature.
          </p>
        ) : undefined}
        <div className="rc-py-2" />
        <div className="rc-flex rc-w-full rc-justify-end">
          <Button variant="light" onClick={props.onCancel}>
            Cancel
          </Button>
          <div className="rc-px-1" />
          <Button
            variant="primary"
            disabled={
              !workbookText || workbookText.length <= 0 || !isOnlineLocal
            }
            onClick={() => {
              props.handleImmersiveInput(workbookText);
              props.handleOpenImmersive(workbookText);
            }}
          >
            Go
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

//Create Immersive Dialog Box
export const ImmersiveDialog = (props: ImmersiveReaderProps) => {
  const isMobileWidth = window.innerWidth <= 800;

  const [initialText, setInitialText] = React.useState<string>();

  React.useEffect(() => {
    if (props.selectedText) {
      setInitialText(props.selectedText);
    }
  }, [props.selectedText]);

  return (
    <Modal isOpen={props.open} size="xl" toggle={props.onCancel} backdrop>
      <ModalBody>
        <ImmersiveReader
          selectedText={initialText || ''}
          getImmersiveToken={props.getImmersiveToken}
          onCancel={props.onCancel}
          toggleIOSInterceptor={props.toggleIOSInterceptor}
        />
      </ModalBody>
    </Modal>
  );
};

export const ImmersiveReader = (props: ImmersiveReaderProps) => {
  const immersiveToken = useSelector(applicationSelectors.getImmersiveToken);

  const immersiveSrc =
    ConfigJson && ConfigJson.ImmersiveSrc
      ? ConfigJson.ImmersiveSrc
      : 'https://readcloud.cognitiveservices.azure.com/immersivereader/webapp/v1.0/reader?exitCallback=ImmersiveReader-Exit&sdkPlatform=js&sdkVersion=1.0.0&cookiePolicy=disable';

  const launchImmersiveReader = (
    immToken?: string
  ): Promise<LaunchResponse> => {
    return new Promise((resolve, reject: (reason: Error) => void): void => {
      const startTime = Date.now();
      const options = {
        uiZIndex: 1000,
        timeout: 15000, // Default to 15 seconds
        useWebview: false,
        allowFullscreen: true,
        hideExitButton: false,
        displayOptions: {
          textSize: 28,
          increaseSpacing: false,
        },
      };
      let timeoutId: number | null = null;

      const data = {
        title: 'ReadCloud Immersive Reader',
        chunks: [
          {
            content: props.selectedText,
          },
        ],
      };

      //Create iframe
      const iframeContainer: HTMLDivElement = document.createElement('div');
      const iframe: HTMLIFrameElement = document.createElement('iframe');
      iframe.setAttribute('id', 'immersiveIframe');

      const resetTimeout = (): void => {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      const reset = (): void => {
        // Remove container along with the iframe inside of it
        if (
          document
            .getElementById('myImmersiveReader')
            ?.contains(iframeContainer)
        ) {
          document
            .getElementById('myImmersiveReader')
            ?.removeChild(iframeContainer);

          props.onCancel();
        }

        window.removeEventListener('message', messageHandler);

        // Clear the timeout timer
        resetTimeout();
      };

      const exit = (): void => {
        if (props.toggleIOSInterceptor) {
          props.toggleIOSInterceptor('on');
        }
        reset();
      };

      const messageHandler = (e: any): void => {
        if (!e || !e.data) {
          return;
        }

        if (e.data === 'ImmersiveReader-ReadyForContent') {
          resetTimeout(); // Reset the timeout once the reader page loads successfully. The Reader page will report further errors through PostMessage if there is an issue obtaining the ContentModel from the server
          const message: Message = {
            cogSvcsAccessToken: immToken,
            cogSvcsSubdomain: 'readcloud',
            request: data,
            launchToPostMessageSentDurationInMs: Date.now() - startTime,
            ...options,
          };

          const postMessageImm = JSON.stringify({
            messageType: 'Content',
            messageValue: message,
          });

          // tslint:disable-next-line: no-non-null-assertion
          iframe?.contentWindow?.postMessage(postMessageImm, '*');
        } else if (e.data === 'ImmersiveReader-Exit') {
          exit();
        } else if (
          e.data &&
          typeof e.data === 'string' &&
          e.data.startsWith('ImmersiveReader-LaunchResponse:')
        ) {
          let launchResponse: LaunchResponse | null = null;
          let error: Error | null = null;

          let response: ImmersiveReaderResponse | null = null;
          try {
            response = JSON.parse(
              e.data.substring('ImmersiveReader-LaunchResponse:'.length)
            );
          } catch {
            // No-op
          }

          if (response && response.success) {
            launchResponse = {
              container: iframeContainer,
              sessionId: response.sessionId,
            };
          } else if (response && !response.success) {
            error = {
              code: response.errorCode ?? ErrorCode.ServerError,
              message: response.errorCode ?? ErrorCode.ServerError,
              sessionId: response?.sessionId,
            };
          } else {
            error = {
              code: ErrorCode.ServerError,
              message: ErrorCode.ServerError,
            };
          }

          if (launchResponse) {
            resetTimeout();
            resolve(launchResponse);
          } else if (error) {
            exit();
            reject(error);
          }
        }
      };

      // Reset variables
      reset();

      window.addEventListener('message', messageHandler);

      // Reject the promise if the Immersive Reader page fails to load.
      timeoutId = window.setTimeout((): void => {
        reset();
        reject({
          code: ErrorCode.Timeout,
          message: `Page failed to load after timeout (${options.timeout} ms)`,
        });
        if (props.toggleIOSInterceptor) {
          props.toggleIOSInterceptor('on');
        }
      }, options.timeout);

      // Create and style iframe
      if (options.allowFullscreen) {
        iframe.setAttribute('allowfullscreen', '');
      }

      //iFrame styles
      iframe.style.cssText =
        'position: relative; width: 100%; height: 80vh; margin-left: auto; margin-right: auto; top: 0; border-width: 0;';

      iframe.src = immersiveSrc;

      //Full Screen Iframe
      //iframeContainer.style.cssText = `position: fixed; width: 100vw; height: 100vh; left: 0; top: 0; border-width: 0; -webkit-perspective: 1px; z-index: ${options.uiZIndex}; background: white; overflow: hidden`;
      iframeContainer.style.cssText = `position: absolute; margin-left: auto; margin-right: auto; width: 100%; height: 85vh; top: 0; border-width: 0; -webkit-perspective: 1px; z-index: ${options.uiZIndex}; background: white; overflow: hidden`;

      iframeContainer.appendChild(iframe);

      const immersiveContainer = document.getElementById('myImmersiveReader');

      if (immersiveContainer && immersiveContainer.firstChild) {
        immersiveContainer.removeChild(immersiveContainer.firstChild);
      }

      immersiveContainer?.appendChild(iframeContainer);
      //setImmersiveReader(iframeContainer);
    });
  };

  React.useEffect(() => {
    props.getImmersiveToken();
  }, []);

  React.useEffect(() => {
    if (props.toggleIOSInterceptor) {
      props.toggleIOSInterceptor('off');
    }
    launchImmersiveReader(immersiveToken);
  }, [immersiveToken]);

  return (
    <div className="rc-w-full rc-flex-shrink-0 rc-p-1">
      <div className="rc-flex rc-w-full">
        <div
          id="myImmersiveReader"
          style={{
            position: 'relative',
            height: '90vh',
            width: '90vw',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        ></div>
      </div>
    </div>
  );
};
